import { API_URL, userRole } from "src/service/config";
import { ILanguageFillter, ILevel } from "src/types";
import React, { useEffect, useState } from "react";

import Layout from "@components/layout";
import { fetchAllLanguages } from "src/service/api-services/api-services";
import fetchWithToken from "@utils/tokenUtils";
import { languageMap } from "src/service/activities.services";
import showToast from "@utils/toast";

interface Props {
  initialLevel?: ILevel;
  levelToUpdateId?: string;
}

const LevelForm = ({ initialLevel, levelToUpdateId }: Props) => {
  const isEditing = !!initialLevel;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [languageList, setLanguages] = useState<ILanguageFillter>({
    isLoading: true,
    languages: [],
  });

  useEffect(() => {
    fetchAllLanguages()
      .then((data) => {
        const { status, result } = data;
        if (status !== "success") {
          setLanguages({ isLoading: false, languages: [] });
          return showToast("Can't fetch languages at the moment", false);
        }
        setLanguages({ isLoading: false, languages: result });
      })
      .catch((error) => {
        const message =
          error instanceof Error
            ? error?.message
            : "Apologies there was an error fetching languages.";
        setLanguages({ isLoading: false, languages: [] });
        return showToast(message, false);
      });
  }, []);

  const [formData, setFormData] = useState({
    icon: initialLevel?.icon || "",
    image: initialLevel?.image || "",
    level: initialLevel?.level || "",
    levelInWords: initialLevel?.levelInWords || "",
    title: initialLevel?.title || "",
    description: initialLevel?.description || "",
    language: initialLevel?.language || "",
    languageId: initialLevel?.languageId || "",
    totalLessons: Number(initialLevel?.totalLessons) ?? "",
    available: initialLevel?.available || false,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const parsedValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!["admin", "editor"].includes(userRole)) {
      showToast("You don't have permission to set level availability", false);
      return;
    }

    const langCode = languageMap[formData.language] || "XX";
    const payload: ILevel = {
      id:
        initialLevel?.id ||
        `LVL-${langCode}-${formData.level.padStart(3, "0")}`,
      ...formData,
    };

    try {
      const url = `${API_URL}/levels${isEditing ? `/${levelToUpdateId}` : ""}`;
      const response = await fetchWithToken(`${url}`, {
        method: isEditing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        showToast(
          `Level ${isEditing ? "updated" : "created"} successfully`,
          true,
        );
        window.location.reload();
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err?.message
          : "There was an error saving level!";
      showToast(message, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="py-5 px-4 mt-6">
        <form
          onSubmit={handleSubmit}
          className="max-w-xl mx-auto p-6 bg-white rounded-lg shadow space-y-6"
        >
          <h1 className="text-lg text-center font-semibold text-gray-800 mb-8">
            {isEditing ? "Edit Level" : "Add New Level"}
          </h1>

          <div>
            <label
              htmlFor="language"
              className="block text-sm font-medium text-gray-700"
            >
              Language
            </label>
            <select
              name="language"
              id="language"
              value={formData.language}
              onChange={handleChange}
              required
              className="input-style"
            >
              <option value="" disabled>
                {languageList.isLoading ? "Loading..." : " Select Language"}
              </option>
              {languageList.languages.map(({ name, code }) => (
                <option key={code} value={name.toLocaleLowerCase()}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <input
              name="level"
              value={formData.level}
              onChange={handleChange}
              required
              type="number"
              placeholder="Level (number)"
              className="input-style"
            />
            <input
              name="levelInWords"
              value={formData.levelInWords}
              onChange={handleChange}
              required
              placeholder="Level (in words)"
              className="input-style"
            />
          </div>

          <input
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="Level Title"
            className="input-style"
          />
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Level Description"
            className="input-style"
          />

          <input
            name="icon"
            value={formData.icon}
            onChange={handleChange}
            placeholder="Icon filename"
            className="input-style"
          />
          <input
            name="image"
            value={formData.image}
            onChange={handleChange}
            placeholder="Image filename"
            className="input-style"
          />

          <input
            type="number"
            placeholder="Total Lessons"
            className="input-style"
            required
            name="totalLessons"
            value={formData.totalLessons}
            onChange={(e) =>
              setFormData({ ...formData, totalLessons: Number(e.target.value) })
            }
          />

          {["admin", "editor"].includes(userRole) && (
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.available}
                  onChange={(e) => {
                    setFormData({ ...formData, available: e.target.checked });
                  }}
                />
                <span>Available</span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="button-primary"
          >
            {isEditing ? "Update Level" : "Create Level"}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default LevelForm;
